import { useState, useEffect, useCallback } from "react";

/**
 * How this works
 * - It looks at the parent portal element (selector) and looks for how many children elements there are.
 * - If there are no child elements it sets interval to look for child elements. If there are, it clears the interval.
 * - The number of child elements must be passed into the hook because it can't figure that out for some reason when it finds the element in the hook
 * - Dashboard tick is incremented when the editor is closed in composer2.js
 * - Reseting child element count to 0 causes it to reset the interval to look for child elements
 */

/**
 *
 * @param {string} selector | The parent portal element.
 * @param {number} childElementCount | The number of child elements of the parent portal element
 * @param {func} setChildCount  | State hook to set the child element count on the parent
 * @param {number} dashboardTick | An incrementing value from Flocknote App State Context
 */
export function useDashboardPortalElement({
  selector,
  childElementCount,
  setChildCount,
  dashboardTick,
}) {
  const [portalToElement, setPortalToElement] = useState(
    document.querySelector(selector)
  );

  const findPortalToElement = useCallback(() => {
    const foundElement = document.querySelector(selector);

    if (foundElement) {
      setPortalToElement(foundElement);
    }
  }, [selector]);

  useEffect(() => {
    // Set interval if there are no child elements
    let interval = setInterval(findPortalToElement, 10);

    if (childElementCount > 0) {
      // Clear the interval when the portal loads child elements
      clearInterval(interval);
    }

    return () => clearInterval(interval);
  }, [childElementCount, findPortalToElement]);

  useEffect(() => {
    // An update of dashboardTick means the editor closed
    setChildCount(0);
  }, [dashboardTick, setChildCount]);

  return portalToElement;
}
